import { Add, ContentCopy, Delete, DoNotDisturb, Link, LinkOff, Person } from '@mui/icons-material';
import {
  Alert,
  Box,
  Button,
  CircularProgress,
  Dialog,
  DialogActions,
  DialogContent,
  DialogTitle,
  IconButton,
  Paper,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
// eslint-disable-next-line no-restricted-imports -- pre-existing direct DataGridPro use; migrate to ServerDataGrid when touched
import { DataGridPro } from '@mui/x-data-grid-pro';
import { format } from 'date-fns';
import React, { useEffect, useMemo, useState } from 'react';
import { useTranslation } from 'react-i18next';
import { useDispatch, useSelector } from 'react-redux';

import { createApiUser, deleteApiUser, fetchApiUsers, resetApiUser, toggleApiUser } from '@sophocles/api/apiUsers';
import { editCustomerIdp, fetchCustomerIdps } from '@sophocles/api/auth';
import { selectDisableThirdPartyApi } from '@sophocles/features/tenant/slice';
import { addToast } from '@sophocles/features/toast';

function TokenValueCard({ label, value, onCopy }) {
  return (
    <Paper variant="outlined" sx={{ p: 2, borderRadius: 2 }}>
      <Stack direction="row" spacing={1} alignItems="flex-start" justifyContent="space-between">
        <Box sx={{ minWidth: 0 }}>
          <Typography variant="overline" color="text.secondary">
            {label}
          </Typography>
          <Typography
            variant="body2"
            sx={{
              fontFamily: 'monospace',
              fontSize: '0.9rem',
              wordBreak: 'break-all',
            }}
          >
            {value}
          </Typography>
        </Box>
        <IconButton size="small" onClick={() => onCopy(value)}>
          <ContentCopy fontSize="small" />
        </IconButton>
      </Stack>
    </Paper>
  );
}

function CustomerAuthenticationTab({ customer, user }) {
  // Third-party API access is dropped on separate-tenant deploys — hide the
  // API-user management block (identity providers still apply).
  const disableThirdPartyApi = useSelector(selectDisableThirdPartyApi);
  const dispatch = useDispatch();
  const [isIdpsLoading, setIsIdpsLoading] = useState(true);
  const [isApiUsersLoading, setIsApiUsersLoading] = useState(true);
  const [idps, setIdps] = useState([]);
  const [apiUsers, setApiUsers] = useState([]);
  const [isCreateDialogOpen, setIsCreateDialogOpen] = useState(false);
  const [revealedToken, setRevealedToken] = useState(null);
  const [newUserName, setNewUserName] = useState('');
  const [toggleConfirmUserId, setToggleConfirmUserId] = useState(null);
  const [deleteConfirmUserId, setDeleteConfirmUserId] = useState(null);
  const [resetConfirmUser, setResetConfirmUser] = useState(null);
  const { t } = useTranslation();

  const customerId = customer.get('id', null);
  const isStaff = user?.get('is_staff', false) ?? false;
  const toggleTargetUser = apiUsers.find(u => u.id === toggleConfirmUserId) ?? null;

  const loadIdps = () => {
    setIsIdpsLoading(true);
    fetchCustomerIdps(customerId)
      .then(data => {
        setIdps(data);
      })
      .catch(error => {
        console.error('Failed to fetch IDPs:', error);
      })
      .finally(() => {
        setIsIdpsLoading(false);
      });
  };

  const loadUsers = () => {
    setIsApiUsersLoading(true);
    fetchApiUsers(customerId)
      .then(users => {
        setApiUsers(users);
      })
      .catch(error => {
        console.error('Failed to fetch API users:', error);
      })
      .finally(() => {
        setIsApiUsersLoading(false);
      });
  };

  useEffect(() => {
    setIsIdpsLoading(true);
    fetchCustomerIdps(customerId)
      .then(data => {
        setIdps(data);
      })
      .catch(error => {
        console.error('Failed to fetch IDPs:', error);
      })
      .finally(() => {
        setIsIdpsLoading(false);
      });

    setIsApiUsersLoading(true);
    fetchApiUsers(customerId)
      .then(users => {
        setApiUsers(users);
      })
      .catch(error => {
        console.error('Failed to fetch API users:', error);
      })
      .finally(() => {
        setIsApiUsersLoading(false);
      });
  }, [customerId]);

  const handleCopy = text => {
    navigator.clipboard.writeText(text);
    dispatch(addToast({ message: t('general.copiedToClipboard'), level: 'success' }));
  };

  const revealApiToken = ({ email, token, clientSecret = null }) => {
    setRevealedToken({
      email,
      token,
      clientSecret,
      authorizationHeader: `Token ${token}`,
    });
  };

  const handleToggleIdp = (id, isEnabled) => {
    editCustomerIdp(customerId, id, { state: isEnabled ? 'off' : 'on' })
      .then(() => {
        loadIdps();
      })
      .catch(error => {
        console.error('Failed to toggle IDP:', error);
        dispatch(addToast({ message: error?.response?.data?.detail ?? 'An error occurred', level: 'error' }));
      });
  };

  const handleOpenCreateDialog = () => {
    setIsCreateDialogOpen(true);
  };

  const handleCloseCreateDialog = () => {
    setIsCreateDialogOpen(false);
    setNewUserName('');
  };

  const handleSubmit = () => {
    createApiUser(newUserName, customerId)
      .then(data => {
        dispatch(addToast({ message: t('auth.apiUserCreated'), level: 'success' }));
        handleCloseCreateDialog();
        revealApiToken({ email: data.user.email, token: data.token, clientSecret: data.client_secret });
        loadUsers();
      })
      .catch(error => {
        dispatch(addToast({ message: error?.response?.data?.detail ?? 'An error occurred', level: 'error' }));
      });
  };

  const handleToggleActive = () => {
    if (!toggleTargetUser) {
      return;
    }

    const nextActive = !toggleTargetUser.is_active;

    toggleApiUser(toggleTargetUser.id, nextActive)
      .then(data => {
        setApiUsers(prevUsers => prevUsers.map(u => (u.id === data.user.id ? data.user : u)));
        dispatch(
          addToast({ message: nextActive ? t('auth.enableAccess') : t('auth.disableAccess'), level: 'success' }),
        );
        setToggleConfirmUserId(null);
      })
      .catch(error => {
        dispatch(addToast({ message: error?.response?.data?.detail ?? 'An error occurred', level: 'error' }));
      });
  };

  const handleResetUser = () => {
    if (!resetConfirmUser) {
      return;
    }

    resetApiUser(resetConfirmUser.id)
      .then(data => {
        dispatch(addToast({ message: t('auth.apiUserReset'), level: 'success' }));
        setResetConfirmUser(null);
        revealApiToken({ email: data.user.email, token: data.token, clientSecret: data.client_secret });
      })
      .catch(error => {
        dispatch(addToast({ message: error?.response?.data?.detail ?? 'An error occurred', level: 'error' }));
      });
  };

  const handleDeleteUser = () => {
    deleteApiUser(deleteConfirmUserId)
      .then(() => {
        dispatch(addToast({ message: t('auth.apiUserDeleted'), level: 'success' }));
        setDeleteConfirmUserId(null);
        loadUsers();
      })
      .catch(error => {
        dispatch(addToast({ message: error?.response?.data?.detail ?? 'An error occurred', level: 'error' }));
      });
  };

  const columns = useMemo(
    () => [
      ...(isStaff
        ? [
            {
              field: 'id',
              headerName: 'ID',
              width: 70,
              sortable: false,
              filterable: false,
              disableColumnMenu: true,
            },
          ]
        : []),
      {
        field: 'first_name',
        headerName: t('general.name'),
        flex: 1,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        valueGetter: (value, row) => `${row.first_name} ${row.last_name}`.trim(),
      },
      {
        field: 'email',
        headerName: t('auth.clientId'),
        flex: 1.5,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: params => (
          <Box sx={{ display: 'flex', alignItems: 'center' }}>
            <Typography variant="body2">{params.value}</Typography>
            <IconButton size="small" sx={{ ml: 0.5 }} onClick={() => handleCopy(params.value)}>
              <ContentCopy fontSize="small" />
            </IconButton>
          </Box>
        ),
      },
      {
        field: 'is_active',
        headerName: t('general.status'),
        width: 100,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: params => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {params.value ? (
              <Person color="primary" fontSize="small" />
            ) : (
              <DoNotDisturb color="disabled" fontSize="small" />
            )}
            <Typography variant="body2">{params.value ? t('general.active') : t('general.inactive')}</Typography>
          </Box>
        ),
      },
      {
        field: 'date_joined',
        headerName: t('general.created'),
        width: 120,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        valueFormatter: value => (value ? format(new Date(value), 'PP') : t('general.never')),
      },
      {
        field: 'last_login',
        headerName: t('auth.lastLogin'),
        width: 180,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        valueFormatter: value => (value ? format(new Date(value), 'PPpp') : t('general.never')),
      },
      {
        field: 'actions',
        headerName: '',
        width: 360,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: params => {
          const { row } = params;
          if (!row.email?.endsWith('@api.outdoorlinkinc.com')) {
            return null;
          }
          return (
            <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
              <Button size="small" variant="outlined" color="warning" onClick={() => setResetConfirmUser(row)}>
                {t('auth.regenerateApiToken')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color={row.is_active ? 'error' : 'primary'}
                onClick={() => setToggleConfirmUserId(row.id)}
              >
                {row.is_active ? t('actions.disable') : t('actions.enable')}
              </Button>
              <Button
                size="small"
                variant="outlined"
                color="error"
                startIcon={<Delete />}
                onClick={() => setDeleteConfirmUserId(row.id)}
              >
                {t('actions.delete')}
              </Button>
            </Box>
          );
        },
      },
    ],
    [t, isStaff],
  );

  const listViewColumn = useMemo(
    () => ({
      field: 'listColumn',
      renderCell: params => {
        const { row } = params;
        return (
          <Box
            sx={{
              py: 1,
              px: 0.5,
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {row.is_active ? (
                <Person color="primary" sx={{ mr: 1 }} />
              ) : (
                <DoNotDisturb color="disabled" sx={{ mr: 1 }} />
              )}
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {`${row.first_name} ${row.last_name}`.trim()}
                </Typography>
                <Box sx={{ display: 'flex', alignItems: 'center' }}>
                  <Typography variant="caption" color="text.secondary">
                    {row.email}
                  </Typography>
                  <IconButton size="small" sx={{ ml: 0.5 }} onClick={() => handleCopy(row.email)}>
                    <ContentCopy sx={{ fontSize: 14 }} />
                  </IconButton>
                </Box>
                <Typography variant="caption" color="text.secondary" display="block">
                  {isStaff && `ID: ${row.id} | `}
                  {t('general.status')}: {row.is_active ? t('general.active') : t('general.inactive')} |{' '}
                  {t('general.created')}:{' '}
                  {row.date_joined ? format(new Date(row.date_joined), 'PP') : t('general.never')} |{' '}
                  {t('auth.lastLogin')}:{' '}
                  {row.last_login ? format(new Date(row.last_login), 'PPpp') : t('general.never')}
                </Typography>
              </Box>
            </Box>
            {row.email?.endsWith('@api.outdoorlinkinc.com') && (
              <Box sx={{ display: 'flex', gap: 0.5, flexShrink: 0, ml: 1 }}>
                <Button size="small" variant="outlined" color="warning" onClick={() => setResetConfirmUser(row)}>
                  {t('auth.regenerateApiToken')}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color={row.is_active ? 'error' : 'primary'}
                  onClick={() => setToggleConfirmUserId(row.id)}
                >
                  {row.is_active ? t('actions.disable') : t('actions.enable')}
                </Button>
                <Button
                  size="small"
                  variant="outlined"
                  color="error"
                  startIcon={<Delete />}
                  onClick={() => setDeleteConfirmUserId(row.id)}
                >
                  {t('actions.delete')}
                </Button>
              </Box>
            )}
          </Box>
        );
      },
    }),
    [t, isStaff],
  );

  const idpColumns = useMemo(
    () => [
      {
        field: 'name',
        headerName: t('general.name'),
        flex: 1,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
      },
      {
        field: 'is_enabled',
        headerName: t('general.status'),
        width: 120,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: params => (
          <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.5 }}>
            {params.value ? <Link color="primary" fontSize="small" /> : <LinkOff color="disabled" fontSize="small" />}
            <Typography variant="body2">{params.value ? t('general.enabled') : t('general.disabled')}</Typography>
          </Box>
        ),
      },
      {
        field: 'actions',
        headerName: '',
        width: 150,
        sortable: false,
        filterable: false,
        disableColumnMenu: true,
        renderCell: params => (
          <Button
            variant="outlined"
            size="small"
            color={params.row.is_enabled ? 'error' : 'primary'}
            onClick={() => handleToggleIdp(params.row.id, params.row.is_enabled)}
            endIcon={params.row.is_enabled ? <LinkOff /> : <Link />}
          >
            {params.row.is_enabled ? t('actions.disable') : t('actions.enable')}
          </Button>
        ),
      },
    ],
    [t],
  );

  const idpListViewColumn = useMemo(
    () => ({
      field: 'listColumn',
      renderCell: params => {
        const { row } = params;
        return (
          <Box
            sx={{
              py: 1,
              px: 0.5,
              width: '100%',
              display: 'flex',
              justifyContent: 'space-between',
              alignItems: 'center',
            }}
          >
            <Box sx={{ display: 'flex', alignItems: 'center' }}>
              {row.is_enabled ? <Link color="primary" sx={{ mr: 1 }} /> : <LinkOff color="disabled" sx={{ mr: 1 }} />}
              <Box>
                <Typography variant="body2" fontWeight={600}>
                  {row.name}
                </Typography>
                <Typography variant="caption" color="text.secondary">
                  {row.is_enabled ? t('general.enabled') : t('general.disabled')}
                </Typography>
              </Box>
            </Box>
            <Button
              variant="outlined"
              size="small"
              color={row.is_enabled ? 'error' : 'primary'}
              onClick={() => handleToggleIdp(row.id, row.is_enabled)}
              endIcon={row.is_enabled ? <LinkOff /> : <Link />}
            >
              {row.is_enabled ? t('actions.disable') : t('actions.enable')}
            </Button>
          </Box>
        );
      },
    }),
    [t],
  );

  if (isIdpsLoading || isApiUsersLoading) {
    return <CircularProgress />;
  }

  return (
    <>
      <Typography variant="h6" sx={{ my: 2 }}>
        {t('userSettings.manageIdps')}
      </Typography>
      <DataGridPro
        rows={idps}
        columns={idpColumns}
        listView
        listViewColumn={idpListViewColumn}
        getRowHeight={() => 'auto'}
        disableRowSelectionOnClick
        disableColumnFilter
        disableColumnSelector
        hideFooter
        density="compact"
        // Border comes from the global MuiDataGrid theme override.
        // Headers hidden because this grid uses listView-style rows.
        sx={{
          '& .MuiDataGrid-columnHeaders': { display: 'none' },
          '& .MuiDataGrid-cell': { py: 1 },
        }}
      />

      {!disableThirdPartyApi && (
        <>
          <Box sx={{ display: 'flex', alignItems: 'center', mt: 3, mb: 1 }}>
            <Typography variant="h6">{t('auth.manageApiAccess')}</Typography>
            <IconButton color="primary" onClick={handleOpenCreateDialog} sx={{ ml: 1 }}>
              <Add />
            </IconButton>
          </Box>
          <Typography variant="body2" color="text.secondary" sx={{ mb: 2 }}>
            {t('auth.apiTokenInstructions')}
          </Typography>

          <DataGridPro
            rows={apiUsers}
            columns={columns}
            listView
            listViewColumn={listViewColumn}
            getRowHeight={() => 'auto'}
            disableRowSelectionOnClick
            disableColumnFilter
            disableColumnSelector
            hideFooter
            density="compact"
            // Border comes from the global MuiDataGrid theme override.
            // Headers hidden because this grid uses listView-style rows.
            sx={{
              '& .MuiDataGrid-columnHeaders': { display: 'none' },
              '& .MuiDataGrid-cell': { py: 1 },
            }}
          />
        </>
      )}

      <Dialog open={isCreateDialogOpen} onClose={handleCloseCreateDialog} fullWidth maxWidth="sm">
        <DialogTitle>{t('auth.addApiUser')}</DialogTitle>
        <DialogContent>
          <Stack spacing={2} sx={{ pt: 1 }}>
            <TextField
              autoFocus
              margin="dense"
              label={t('general.name')}
              type="text"
              fullWidth
              variant="outlined"
              value={newUserName}
              onChange={event => setNewUserName(event.target.value)}
            />
            <Typography variant="body2" color="text.secondary">
              {t('auth.apiTokenShownOnce')}
            </Typography>
          </Stack>
        </DialogContent>
        <DialogActions>
          <Button onClick={handleCloseCreateDialog}>{t('actions.cancel')}</Button>
          <Button onClick={handleSubmit} disabled={newUserName.trim() === ''}>
            {t('actions.create')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={revealedToken !== null} onClose={() => setRevealedToken(null)} fullWidth maxWidth="sm">
        <DialogTitle>{t('auth.apiUserCredentials')}</DialogTitle>
        <DialogContent>
          {revealedToken && (
            <Stack spacing={2} sx={{ pt: 1 }}>
              <Alert severity="warning">{t('auth.apiTokenShownOnce')}</Alert>
              <Typography variant="body2" color="text.secondary">
                {t('auth.apiTokenHelp')}
              </Typography>
              <TokenValueCard label={t('auth.clientId')} value={revealedToken.email} onCopy={handleCopy} />
              {revealedToken.clientSecret && (
                <TokenValueCard label={t('auth.clientSecret')} value={revealedToken.clientSecret} onCopy={handleCopy} />
              )}
              <TokenValueCard label={t('auth.apiAccessToken')} value={revealedToken.token} onCopy={handleCopy} />
              <TokenValueCard
                label={t('auth.authorizationHeader')}
                value={revealedToken.authorizationHeader}
                onCopy={handleCopy}
              />
              <Typography variant="body2" color="error">
                {t('auth.apiMessage')}
              </Typography>
            </Stack>
          )}
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setRevealedToken(null)}>{t('actions.close')}</Button>
        </DialogActions>
      </Dialog>

      <Dialog open={resetConfirmUser !== null} onClose={() => setResetConfirmUser(null)}>
        <DialogTitle>{t('auth.resetApiUser')}</DialogTitle>
        <DialogContent>
          <Typography>{t('auth.resetApiUserConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setResetConfirmUser(null)}>{t('actions.cancel')}</Button>
          <Button onClick={handleResetUser} color="warning">
            {t('auth.regenerateApiToken')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={deleteConfirmUserId !== null} onClose={() => setDeleteConfirmUserId(null)}>
        <DialogTitle>{t('auth.deleteApiUser')}</DialogTitle>
        <DialogContent>
          <Typography>{t('auth.deleteApiUserConfirm')}</Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setDeleteConfirmUserId(null)}>{t('actions.cancel')}</Button>
          <Button onClick={handleDeleteUser} color="error">
            {t('actions.delete')}
          </Button>
        </DialogActions>
      </Dialog>

      <Dialog open={toggleConfirmUserId !== null} onClose={() => setToggleConfirmUserId(null)}>
        <DialogTitle>{toggleTargetUser?.is_active ? t('auth.disableApiUser') : t('auth.enableApiUser')}</DialogTitle>
        <DialogContent>
          <Typography>
            {toggleTargetUser?.is_active ? t('auth.disableApiUserConfirm') : t('auth.enableApiUserConfirm')}
          </Typography>
        </DialogContent>
        <DialogActions>
          <Button onClick={() => setToggleConfirmUserId(null)}>{t('actions.cancel')}</Button>
          <Button onClick={handleToggleActive} color={toggleTargetUser?.is_active ? 'error' : 'primary'}>
            {toggleTargetUser?.is_active ? t('actions.disable') : t('actions.enable')}
          </Button>
        </DialogActions>
      </Dialog>
    </>
  );
}

export default CustomerAuthenticationTab;
